import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { logout as logoutApi } from '../services/api';
import ImageWithFallback from './ImageWithFallback';

const StudentSidebar = ({ onClose, isPersistent = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        if (!isPersistent) {
            const timer = setTimeout(() => setIsAnimating(true), 10);
            return () => clearTimeout(timer);
        }
    }, [isPersistent]);

    const handleClose = () => {
        if (isPersistent) return;
        setIsAnimating(false);
        setTimeout(onClose, 300);
    };

    const handleLogout = async () => {
        try {
            await logoutApi();
        } catch (error) {
            console.error("Logout API failed:", error);
        }
        logout();
        navigate('/');
        if (!isPersistent && onClose) handleClose();
    };

    const isActive = (path) => {
        return location.pathname === path;
    };

    const handleNavigation = (path) => {
        navigate(path);
        if (!isPersistent && onClose) handleClose();
    };

    // Shared sidebar content (used in both modes)
    const sidebarContent = (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className={`flex items-center ${isPersistent ? '' : 'justify-between'} p-4 border-b border-gray-200`}>
                <div className="flex items-center gap-3">
                    {user?.image ? (
                        <ImageWithFallback
                            src={user.image}
                            alt="Profile"
                            className="w-10 h-10 rounded-full object-cover border-2 border-gray-300"
                        />
                    ) : (
                        <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-bold text-lg border-2 border-gray-300">
                            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                    )}
                    <h3 className="font-semibold text-gray-900">{user?.name}</h3>
                </div>
                {!isPersistent && (
                    <button
                        onClick={handleClose}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        <i className="fas fa-times text-gray-500"></i>
                    </button>
                )}
            </div>

            {/* Navigation */}
            <div className="flex-1 py-6">
                <nav className="px-4 space-y-2">
                    <button
                        onClick={() => handleNavigation('/')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive('/')
                            ? 'bg-emerald-50 text-emerald-600'
                            : 'text-gray-700 hover:bg-gray-50'
                            }`}
                    >
                        <i className="fas fa-home w-5"></i>
                        <span className="font-medium">Home</span>
                    </button>

                    <button
                        onClick={() => handleNavigation('/student/dashboard')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive('/student/dashboard')
                            ? 'bg-emerald-50 text-emerald-600'
                            : 'text-gray-700 hover:bg-gray-50'
                            }`}
                    >
                        <i className="fas fa-columns w-5"></i>
                        <span className="font-medium">Dashboard</span>
                    </button>

                    <button
                        onClick={() => handleNavigation('/student/MyCourses')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive('/student/MyCourses')
                            ? 'bg-emerald-50 text-emerald-600'
                            : 'text-gray-700 hover:bg-gray-50'
                            }`}
                    >
                        <i className="fas fa-book w-5"></i>
                        <span className="font-medium">My Courses</span>
                    </button>

                    <button
                        onClick={() => handleNavigation('/student/certificates')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive('/student/certificates')
                            ? 'bg-emerald-50 text-emerald-600'
                            : 'text-gray-700 hover:bg-gray-50'
                            }`}
                    >
                        <i className="fas fa-certificate w-5"></i>
                        <span className="font-medium">Certificates</span>
                    </button>

                    <button
                        onClick={() => handleNavigation('/student/profile')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive('/student/profile')
                            ? 'bg-emerald-50 text-emerald-600'
                            : 'text-gray-700 hover:bg-gray-50'
                            }`}
                    >
                        <i className="fas fa-user w-5"></i>
                        <span className="font-medium">Profile</span>
                    </button>

                    <button
                        onClick={() => handleNavigation('/student/support')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive('/student/support')
                            ? 'bg-emerald-50 text-emerald-600'
                            : 'text-gray-700 hover:bg-gray-50'
                            }`}
                    >
                        <i className="fas fa-headset w-5"></i>
                        <span className="font-medium">Support</span>
                    </button>
                </nav>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4">
                <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                >
                    <i className="fas fa-sign-out-alt w-5"></i>
                    <span className="font-medium">Logout</span>
                </button>
            </div>
        </div>
    );

    // Persistent mode: just the content, no overlay
    if (isPersistent) {
        return sidebarContent;
    }

    // Overlay mode: with dark backdrop and slide animation
    return (
        <>
            <div
                className={`fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] transition-opacity duration-300 ease-in-out ${isAnimating ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                onClick={handleClose}
            ></div>
            <div
                className={`fixed top-0 left-0 h-full w-80 bg-white shadow-2xl z-[101] transform transition-transform duration-300 ease-in-out ${isAnimating ? 'translate-x-0' : '-translate-x-full'
                    }`}
            >
                {sidebarContent}
            </div>
        </>
    );
};

export default StudentSidebar;
